import { HTMLAttributes } from 'react';
import { Button } from '../common/Button';
import '../../styles/upgrade-prompt.css';

export type UpgradeTrigger = 'cv_limit' | 'vacancy_fifo' | 'cover_letter' | 'progressive' | 'upgrade_paid';

export interface UpgradePromptProps extends HTMLAttributes<HTMLDivElement> {
  variant: 'modal' | 'toast' | 'banner';
  trigger: UpgradeTrigger;
  quotaInfo?: {
    remaining: number;
    limit: number;
    type: string;
  };
  onUpgrade: () => void;
  onDismiss: () => void;
  isOpen: boolean;
}

/**
 * UpgradePrompt - Display upgrade prompts when users hit limits
 *
 * Variants:
 * - modal: Full-screen blocking modal (for CV generation limit)
 * - toast: Non-blocking notification (for progressive nudges)
 * - banner: Persistent top banner (for FIFO warnings)
 *
 * Triggers:
 * - cv_limit: User hit 5 CV generation limit
 * - vacancy_fifo: User approaching/at 10 vacancy limit
 * - cover_letter: User hit 2 cover letter limit
 * - progressive: Gentle nudges throughout journey
 */
export function UpgradePrompt({
  variant,
  trigger,
  quotaInfo,
  onUpgrade,
  onDismiss,
  isOpen,
  className = '',
  ...props
}: UpgradePromptProps) {
  if (!isOpen) return null;

  const config = getPromptConfig(trigger, quotaInfo);

  return (
    <>
      {/* Backdrop for modal variant */}
      {variant === 'modal' && (
        <div
          className="upgrade-prompt__backdrop"
          onClick={config.dismissable ? onDismiss : undefined}
          aria-hidden="true"
        />
      )}

      <div
        className={`upgrade-prompt upgrade-prompt--${variant} ${className}`}
        role={variant === 'modal' ? 'dialog' : 'alert'}
        aria-live={variant === 'modal' ? undefined : 'polite'}
        {...props}
      >
        <div className="upgrade-prompt__content">
          {/* Icon */}
          <div className="upgrade-prompt__icon-wrapper">
            <span className="upgrade-prompt__icon" role="img" aria-label={config.iconLabel}>
              {config.icon}
            </span>
          </div>

          {/* Title */}
          <h2 className="upgrade-prompt__title">{config.title}</h2>

          {/* Description */}
          <p className="upgrade-prompt__description">{config.description}</p>

          {/* Benefits list (modal only) */}
          {variant === 'modal' && (
            <ul className="upgrade-prompt__benefits">
              {config.benefits.map((benefit, i) => (
                <li key={i} className="upgrade-prompt__benefit-item">
                  <svg
                    className="upgrade-prompt__check-icon"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Actions */}
          <div className="upgrade-prompt__actions">
            <Button variant="primary" onClick={onUpgrade} className="upgrade-prompt__cta">
              {config.ctaText}
            </Button>

            {config.dismissable && (
              <Button
                variant="ghost"
                onClick={onDismiss}
                className="upgrade-prompt__dismiss"
              >
                Maybe Later
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Get prompt configuration based on trigger
 */
function getPromptConfig(trigger: UpgradeTrigger, quotaInfo?: UpgradePromptProps['quotaInfo']) {
  const remaining = quotaInfo?.remaining ?? 0;

  const configs = {
    cv_limit: {
      icon: '🎉',
      iconLabel: 'Celebration',
      title: remaining > 0
        ? `You've generated ${5 - remaining} CVs!`
        : "You've generated 5 amazing CVs!",
      description: remaining > 0
        ? "Create a free account to get unlimited CV generation, cloud sync, and never lose your data."
        : "You've proven this tool works for you! Create a free account to keep generating tailored CVs.",
      benefits: [
        'Generate unlimited tailored CVs',
        'Save all your jobs (no 10-job limit)',
        'Access from any device (cloud sync)',
        'Never lose your data',
      ],
      ctaText: 'Create Free Account',
      dismissable: false, // Hard blocker at limit
    },
    vacancy_fifo: {
      icon: '💡',
      iconLabel: 'Tip',
      title: 'Sign up free to keep all your jobs',
      description: `${remaining} free slots left. Sign up to save all your researched jobs forever — no limits, no data loss.`,
      benefits: [
        'Keep all your researched jobs forever',
        'Cloud sync across devices',
        'Access your jobs from anywhere',
      ],
      ctaText: 'Create Free Account',
      dismissable: true,
    },
    cover_letter: {
      icon: '📧',
      iconLabel: 'Mail',
      title: "You've used 2 free cover letters",
      description: 'Create account for unlimited cover letter generation and premium templates.',
      benefits: [
        'Unlimited cover letter generation',
        'Premium templates',
        'Cloud sync',
      ],
      ctaText: 'Create Account',
      dismissable: true,
    },
    progressive: {
      icon: '✨',
      iconLabel: 'Sparkles',
      title: `${remaining} free CVs remaining`,
      description: 'Create account anytime for unlimited CV generation and cloud sync.',
      benefits: [
        'Unlimited CV generation',
        'Cloud sync across devices',
        'Never lose your data',
      ],
      ctaText: 'Create Free Account',
      dismissable: true,
    },
    upgrade_paid: {
      icon: '🚀',
      iconLabel: 'Rocket',
      title: "You've reached your free limit",
      description: 'Upgrade to Pro for more analyses, CV generations, and priority AI.',
      benefits: [
        '50 ATS analyses / month',
        '20 tailored CV generations / month',
        'Unlimited job tracking',
        'Priority AI (faster responses)',
      ],
      ctaText: 'Upgrade to Pro — from $8.25/mo',
      dismissable: true,
    },
  };

  return configs[trigger];
}
